import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconArrowRight } from '@iwoca/icons';
import { Trans } from '@lingui/react/macro';
import { FormProvider, useForm } from 'react-hook-form';

import AddressField from './Fields/AddressField/AddressField';
import DateOfBirthField from './Fields/DateOfBirthField/DateOfBirthField';
import FullNameField from './Fields/FullNameField/FullNameField';
import PhoneNumberField from './Fields/PhoneNumberField/PhoneNumberField';
import PrivacyPolicyField from './Fields/PrivacyPolicyField/PrivacyPolicyField';
import styles from './PersonalDetailsDE.module.css';
import { schema } from './PersonalDetailsDE.schema';
import {
  useGetSelectStateQuery,
  useGetStateQuery,
} from '../../../api/lendingApi/lendingApiQueries';
import { useContextStateKey } from '../../../hooks/useContextStateKey';
import { useSignupRouterDE } from '../../../hooks/useSignupRouter/useSignupRouterDE';
import { TGetStateResponseDE } from '../../../lapiOverrideTypesDE';
import { getAmplitudeDefaultEventProperties } from '../../../utils/amplitudeEvents';
import AdditionalInformation from '../../AdditionalInformation/AdditionalInformation';
import { Button } from '../../Button/Button';
import { EditAmount } from '../../EditAmount/EditAmount';
import {
  denormalisePersonalDataDE,
  normaliseStepDataDE,
} from '../../helpers/stateNormalisation';
import { APPLICANT_STEP_ID } from '../../helpers/steps';
import AccountCreatedModal from '../../SignupRoutes/AccountCreatedModal/AccountCreatedModal';

export type TPersonalDetailsDataDE = {
  title: 'herr' | 'frau';
  firstName: string;
  lastName: string;
  dobDay: string;
  dobMonth: string;
  dobYear: string;
  phoneNumber: string;
  houseNumber: string;
  streetLine1: string;
  streetLine2?: string | undefined;
  postcode: string;
  town: string;
  privacyPolicyAgreement: boolean;
};

type TProps = {
  handleStateUpdateError: (error: Error) => void;
};

const PersonalDetailsDE = ({ handleStateUpdateError }: TProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNavigatingBack, setIsNavigatingBack] = useState(false);

  const { state, isStateFetching } = useGetStateQuery<TGetStateResponseDE>();

  const { normalisedState } = useGetSelectStateQuery(
    (data) => normaliseStepDataDE(data)[APPLICANT_STEP_ID],
  );

  const stateKey = useContextStateKey();
  const { navigateSignupDE } = useSignupRouterDE();

  const methods = useForm<TPersonalDetailsDataDE>({
    resolver: yupResolver(schema),
    defaultValues: { ...normalisedState },
  });

  useEffect(() => {
    tracking.ampli.stepLoaded({
      'Step name': 'Personal details',
      ...getAmplitudeDefaultEventProperties(),
    });
  }, []);

  const { handleSubmit } = methods;

  const navigateBack = async () => {
    setIsNavigatingBack(true);

    tracking.ampli.backCtaClicked(getAmplitudeDefaultEventProperties());

    await navigateSignupDE('schritt-1', stateKey);
    setIsNavigatingBack(false);
  };

  const onSubmit = async (values: TPersonalDetailsDataDE) => {
    if (!state || !stateKey) {
      return;
    }

    const denormalisedState = denormalisePersonalDataDE(state, values);

    tracking.ampli.personalDetailsSubmitted(
      getAmplitudeDefaultEventProperties(),
    );

    try {
      setIsSubmitting(true);
      await navigateSignupDE('angebote', stateKey, denormalisedState.data);
    } catch (error: unknown) {
      if (error instanceof Error) {
        handleStateUpdateError(error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className={styles.container}>
        <EditAmount currentStep="personal_details" />
        <h1 className={styles.heading}>
          <Trans>Tell us about yourself</Trans>
        </h1>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.formFieldContainer}>
              <FullNameField />
              <DateOfBirthField />
              <PhoneNumberField />
              <AddressField />
              <PrivacyPolicyField />
            </div>
            <div className={styles.buttonsContainer}>
              <Button
                variant="tertiary"
                className={styles.backButton}
                disabled={isSubmitting || isStateFetching}
                loading={isNavigatingBack}
                onClick={navigateBack}
                type="button"
              >
                <Trans>Back</Trans>
              </Button>
              <Button
                className={styles.button}
                disabled={isNavigatingBack}
                loading={isSubmitting || isStateFetching}
                type="submit"
              >
                <Trans>Get decision</Trans>
                <IconArrowRight />
              </Button>
            </div>
          </form>
        </FormProvider>
        <div className={styles.additionalInfoContainer}>
          <AdditionalInformation />
        </div>
      </div>
      {stateKey && <AccountCreatedModal stateKey={stateKey} />}
    </>
  );
};

export default PersonalDetailsDE;
